import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import Select from "react-select";
import Chart from 'react-apexcharts';
import { API_BASE_URL } from '../../config.js'

const TYPE_OPTIONS = [
	{ value: 'salary', label: 'Net Salary' },
	{ value: 'pf_esi', label: 'PF & ESI' },
	// employee joined / left reports from xin_employees
	{ value: 'emp_3', label: '3 Month Employee List', months: 3 },
	{ value: 'emp_6', label: '6 Month Employee List', months: 6 },
	{ value: 'emp_12', label: '1 Year Employee List', months: 12 },
	// HR-approved leaves from xin_leave_applications
	{ value: 'leave_3', label: '3 Month Leave Details', months: 3, leave: true },
	{ value: 'leave_6', label: '6 Month Leave Details', months: 6, leave: true },
	{ value: 'leave_12', label: '1 Year Leave Details', months: 12, leave: true },
	// rostered days with no clock in and no clock out
	{ value: 'absent_3', label: '3 Month Absent Count', months: 3, absent: true },
	{ value: 'absent_6', label: '6 Month Absent Count', months: 6, absent: true },
	{ value: 'absent_12', label: '1 Year Absent Count', months: 12, absent: true },
];

const LEAVE_COLORS = ['#01B8AA', '#374649', '#FD625E', '#F2C80F', '#5F6B6D', '#8AD4EB', '#FE9666', '#A66999', '#3599B8', '#DFBFBF'];

// Power BI style palette: teal = this year, dark slate = last year
const COLORS = ['#01B8AA', '#374649', '#FD625E'];
const FONT = '"Segoe UI", "Helvetica Neue", Arial, sans-serif';

// 7181506 -> "71.8L", 12000000 -> "1.2Cr"
const compact = (v) => {
	const n = Number(v || 0);
	if (Math.abs(n) >= 1e7) return (n / 1e7).toFixed(1) + 'Cr';
	if (Math.abs(n) >= 1e5) return (n / 1e5).toFixed(1) + 'L';
	if (Math.abs(n) >= 1e3) return (n / 1e3).toFixed(1) + 'K';
	return String(Math.round(n));
};

const cardStyle = { borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,.12)', border: 'none' };
const titleStyle = { fontFamily: FONT, fontSize: 18, color: '#252423', margin: 0, fontWeight: 400 };
const subTitleStyle = { fontFamily: FONT, fontSize: 11, color: '#605E5C', letterSpacing: '.5px', textTransform: 'uppercase' };

const selectStyles = {
	control: (base) => ({ ...base, minHeight: "36px", borderColor: "#ced4da", boxShadow: "none", fontSize: "13px" }),
	menu: (base) => ({ ...base, zIndex: 9999 }),
	option: (base) => ({ ...base, fontSize: "13px" }),
};

// first year with generated salary
const FIRST_YEAR = 2019;

const inr = (v) => Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });


const PayrollBI = (props) => {
	const { fixNavbar } = props;

	// current year -> Jan to current month, previous years -> full Jan to Dec
	const currentYear = new Date().getFullYear();
	const yearOptions = [];
	for (let y = currentYear; y >= FIRST_YEAR; y--) yearOptions.push({ value: String(y), label: String(y) });

	const [filter, setFilter] = useState({
		year: String(currentYear),
		department_id: '0',
		type: 'salary',
	});
	const [departments, setDepartments] = useState([]);
	const [trend, setTrend] = useState(null);
	const [breakdown, setBreakdown] = useState(null);
	const [loading, setLoading] = useState(false);
	const [err, setErr] = useState(null);
	const [empData, setEmpData] = useState(null);
	const [leaveData, setLeaveData] = useState(null);
	const [absentData, setAbsentData] = useState(null);

	const token = localStorage.getItem('authToken');

	useEffect(() => {
		axios.get(API_BASE_URL + '/showdepartment', {
			headers: { Authorization: `Bearer ${token}` }
		})
			.then(res => setDepartments(res.data || []))
			.catch(e => console.log(e));
	}, []);

	// reload both charts whenever year / department / type changes
	useEffect(() => {
		if (!filter.year) return;
		const config = { headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' } };
		setLoading(true);
		setErr(null);
		const selType = TYPE_OPTIONS.find(t => t.value === filter.type);
		if (selType?.months) {
			const url = selType.leave ? '/payrollBILeaves' : selType.absent ? '/payrollBIAbsents' : '/payrollBIEmployees';
			axios.get(API_BASE_URL + url, {
				...config, params: { department_id: filter.department_id, months: selType.months, year: filter.year }
			})
				.then(res => selType.leave ? setLeaveData(res.data) : selType.absent ? setAbsentData(res.data) : setEmpData(res.data))
				.catch(e => setErr(e?.response?.data?.message || e.message || 'Failed to load data'))
				.finally(() => setLoading(false));
			return;
		}
		Promise.all([
			axios.get(API_BASE_URL + '/payrollBITrend', { ...config, params: filter }),
			axios.get(API_BASE_URL + '/payrollBI', { ...config, params: filter }),
		])
			.then(([t, b]) => { setTrend(t.data); setBreakdown(b.data); })
			.catch(e => setErr(e?.response?.data?.message || e.message || 'Failed to load data'))
			.finally(() => setLoading(false));
	}, [filter.year, filter.department_id, filter.type]);

	const departmentOptions = [
		{ value: '0', label: 'All Department' },
		...departments.map(d => ({ value: String(d.department_id), label: d.department_name }))
	];
	const deptLabel = departmentOptions.find(o => o.value === filter.department_id)?.label;
	const typeLabel = TYPE_OPTIONS.find(t => t.value === filter.type).label;
	const selectedType = TYPE_OPTIONS.find(t => t.value === filter.type);
	const isLeaveReport = !!selectedType.leave;
	const isAbsentReport = !!selectedType.absent;
	const isEmpReport = !!selectedType.months && !isLeaveReport && !isAbsentReport;
	// "Jan 2026 – Oct 2026", from the API so it matches the months actually queried
	const periodLabel = breakdown ? `${breakdown.from} – ${breakdown.to}` : filter.year;

	const baseChart = {
		fontFamily: FONT,
		foreColor: '#605E5C',
		toolbar: { show: true, tools: { download: true, selection: false, zoom: false, zoomin: false, zoomout: false, pan: false, reset: false } },
	};
	const baseLegend = {
		position: 'top', horizontalAlign: 'left', fontSize: '13px',
		markers: { width: 11, height: 11, radius: 11 },
		itemMargin: { horizontal: 8 },
	};
	const baseGrid = { borderColor: '#EDEBE9', strokeDashArray: 0, xaxis: { lines: { show: false } } };

	// ===== month-wise trend: this year vs same period last year =====
	const isPfEsi = filter.type === 'pf_esi';
	const hasTrend = trend && [...trend.current, ...(trend.esi || [])].some(v => v > 0);
	// Net Salary is compared with Gross Salary; PF & ESI shown side by side
	const compareLabel = isPfEsi ? 'ESI' : 'Gross Salary';
	const trendSeries = trend ? [
		{ name: isPfEsi ? 'PF' : typeLabel, data: trend.current },
		{ name: compareLabel, data: isPfEsi ? (trend.esi || []) : trend.gross },
	] : [];
	const trendOptions = {
		chart: { ...baseChart, id: 'payroll-bi-trend' },
		colors: COLORS,
		plotOptions: { bar: { columnWidth: '70%' } },
		dataLabels: { enabled: false },
		legend: baseLegend,
		grid: baseGrid,
		xaxis: {
			categories: trend?.labels || [],
			axisBorder: { show: false },
			axisTicks: { show: false },
		},
		yaxis: { min: 0, labels: { formatter: compact } },
		tooltip: {
			shared: true,
			intersect: false,
			y: { formatter: (v) => v == null ? '-' : '₹ ' + inr(v) }
		},
	};

	// ===== department / employee breakdown for the selected month =====
	const bdLabels = breakdown?.labels || [];
	const bdOptions = {
		chart: { ...baseChart, id: 'payroll-bi-breakdown', stacked: false },
		colors: COLORS,
		plotOptions: { bar: { columnWidth: bdLabels.length > 10 ? '70%' : '45%' } },
		dataLabels: { enabled: false },
		legend: baseLegend,
		grid: baseGrid,
		xaxis: {
			categories: bdLabels,
			axisBorder: { show: false },
			axisTicks: { show: false },
			labels: { rotate: -45, trim: true, maxHeight: 120, style: { fontSize: '11px' } }
		},
		yaxis: { labels: { formatter: compact } },
		tooltip: { shared: true, intersect: false, y: { formatter: (v) => '₹ ' + inr(v) } },
	};

	// ===== employee joined / left report =====
	const empOptions = {
		chart: { ...baseChart, id: 'payroll-bi-employees' },
		colors: ['#01B8AA', '#FD625E'],
		plotOptions: { bar: { columnWidth: '50%' } },
		dataLabels: { enabled: true, style: { fontSize: '11px' } },
		legend: baseLegend,
		grid: baseGrid,
		xaxis: { categories: empData?.labels || [], axisBorder: { show: false }, axisTicks: { show: false } },
		yaxis: { min: 0, forceNiceScale: true, labels: { formatter: (v) => Math.round(v) } },
		tooltip: { shared: true, intersect: false },
	};
	const empSeries = empData ? [
		{ name: 'New Joined', data: empData.joined },
		{ name: 'Quit', data: empData.left },
	] : [];
	const fmtDate = (d) => d ? d.split('-').reverse().join('-') : '-';
	const kpi = (label, value, color) => (
		<div className="col-md-3 col-6 mb-3" key={label}>
			<div style={{ borderLeft: `4px solid ${color}`, padding: '8px 12px', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,.08)' }}>
				<div style={subTitleStyle}>{label}</div>
				<div style={{ fontFamily: FONT, fontSize: 26, color: '#252423' }}>{value}</div>
			</div>
		</div>
	);
	const empTable = (title, rows) => (
		<div className="card" style={cardStyle}>
			<div className="card-body">
				<h4 style={titleStyle}>{title}</h4>
				<div style={subTitleStyle}>{rows.length} employees · {deptLabel}</div>
				<div className="table-responsive mt-2" style={{ maxHeight: 420, overflowY: 'auto' }}>
					<table className="table table-hover table-striped table-vcenter mb-0" style={{ fontSize: 13 }}>
						<thead>
							<tr>
								<th>#</th><th>Emp ID</th><th>Name</th><th>Department</th><th>Designation</th>
								<th>Joining Date</th><th>Leaving Date</th><th>Status</th>
							</tr>
						</thead>
						<tbody>
							{rows.length === 0 && <tr><td colSpan="8" className="text-center">No employees</td></tr>}
							{rows.map((e, i) => (
								<tr key={e.employee_id + '-' + i}>
									<td>{i + 1}</td>
									<td>{e.employee_id}</td>
									<td>{e.name}</td>
									<td>{e.department_name || '-'}</td>
									<td>{e.designation_name || '-'}</td>
									<td>{fmtDate(e.date_of_joining)}</td>
									<td>{fmtDate(e.date_of_leaving)}</td>
									<td>
										{Number(e.is_active) === 1
											? <span className="tag tag-success">Active</span>
											: <span className="tag tag-danger">Inactive</span>}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	);

	// ===== HR-approved leave report =====
	const leaveOptions = {
		chart: { ...baseChart, id: 'payroll-bi-leaves', stacked: true },
		colors: LEAVE_COLORS,
		plotOptions: { bar: { columnWidth: '50%' } },
		dataLabels: { enabled: false },
		legend: baseLegend,
		grid: baseGrid,
		xaxis: { categories: leaveData?.labels || [], axisBorder: { show: false }, axisTicks: { show: false } },
		yaxis: { min: 0, forceNiceScale: true, labels: { formatter: (v) => Math.round(v) } },
		tooltip: { shared: true, intersect: false, y: { formatter: (v) => v + ' days' } },
	};

	const leaveTypeOptions = {
		chart: { ...baseChart, id: 'payroll-bi-leave-types' },
		colors: LEAVE_COLORS,
		labels: (leaveData?.type_totals || []).map(t => t.name),
		legend: { ...baseLegend, position: 'right' },
		dataLabels: { enabled: true, formatter: (v) => v.toFixed(1) + ' %' },
		tooltip: { y: { formatter: (v) => v + ' days' } },
		plotOptions: { pie: { donut: { size: '55%' } } },
	};

	// department list for "All Department", employee list once a department is picked
	const leaveByDept = filter.department_id === '0';
	const leaveRows = leaveByDept ? (leaveData?.departments || []) : (leaveData?.employees || []);

	// ===== absent (no clock in / clock out) report =====
	const absentOptions = {
		chart: { ...baseChart, id: 'payroll-bi-absents' },
		colors: ['#FD625E'],
		plotOptions: { bar: { columnWidth: '50%' } },
		dataLabels: { enabled: true, style: { fontSize: '11px' } },
		legend: baseLegend,
		grid: baseGrid,
		xaxis: { categories: absentData?.labels || [], axisBorder: { show: false }, axisTicks: { show: false } },
		yaxis: { min: 0, forceNiceScale: true, labels: { formatter: (v) => Math.round(v) } },
		tooltip: { y: { formatter: (v) => v + ' days' } },
	};
	const absentRows = leaveByDept ? (absentData?.departments || []) : (absentData?.employees || []);

	const noData = <div style={{ fontFamily: FONT, color: '#605E5C', padding: '40px 0' }}>No salary generated for {periodLabel}.</div>;

	return (
		<div className={`section-body ${fixNavbar ? "marginTop" : ""} mt-3`}>
			<div className="container-fluid">
				<div className="card" style={cardStyle}>
					<div className="card-body pb-0">
						<div className="row">
							{/* employee / leave reports: current year -> last completed months, past year -> last months of that year */}
							<div className="col-md-3 col-sm-6">
								<div className="form-group">
									<label className="form-label">Select Year</label>
									<Select
										value={yearOptions.find(o => o.value === filter.year)}
										onChange={sel => setFilter({ ...filter, year: sel.value })}
										options={yearOptions}
										isSearchable={false}
										styles={selectStyles}
									/>
								</div>
							</div>
							<div className="col-md-4 col-sm-6">
								<div className="form-group">
									<label className="form-label">Department</label>
									<Select
										value={departmentOptions.find(o => o.value === filter.department_id)}
										onChange={sel => setFilter({ ...filter, department_id: sel ? sel.value : '0' })}
										options={departmentOptions}
										isSearchable={true}
										styles={selectStyles}
									/>
								</div>
							</div>
							<div className="col-md-3 col-sm-6">
								<div className="form-group">
									<label className="form-label">Report</label>
									<Select
										value={TYPE_OPTIONS.find(o => o.value === filter.type)}
										onChange={sel => setFilter({ ...filter, type: sel.value })}
										options={TYPE_OPTIONS}
										isSearchable={false}
										styles={selectStyles}
									/>
								</div>
							</div>
						</div>
					</div>
				</div>

				{err && <div className="alert alert-danger">{err}</div>}

				{isEmpReport && loading && <div className="card" style={cardStyle}><div className="card-body">Loading…</div></div>}
				{isEmpReport && !loading && !err && empData && (<>
					<div className="row">
						{kpi('New Joined', empData.counts.joined, '#01B8AA')}
						{kpi('Quit', empData.counts.left, '#FD625E')}
					</div>
					<div className="card" style={cardStyle}>
						<div className="card-body">
							<h4 style={titleStyle}>New Joined, Quit</h4>
							<div style={subTitleStyle}>
								By month · {deptLabel} · {fmtDate(empData.from)} to {fmtDate(empData.to)}
							</div>
							<Chart options={empOptions} series={empSeries} type="bar" width="100%" height="350" />
						</div>
					</div>
					{empTable('New Joined Employees', empData.joined_list)}
					{empTable('Quit Employees', empData.left_list)}
				</>)}

				{isLeaveReport && loading && <div className="card" style={cardStyle}><div className="card-body">Loading…</div></div>}
				{isLeaveReport && !loading && !err && leaveData && (<>
					<div className="row">
						{kpi('Approved Leave Days', leaveData.counts.days, '#01B8AA')}
						{kpi('Rejected Leave Days', leaveData.counts.rejected_days, '#F2C80F')}
						{kpi('Leave Requests', leaveData.counts.requests, '#3599B8')}
						{kpi('Employees', leaveData.counts.employees, '#FD625E')}
					</div>
					<div className="card" style={cardStyle}>
						<div className="card-body">
							<h4 style={titleStyle}>Leave Days by Leave Type</h4>
							<div style={subTitleStyle}>
								By month · {deptLabel} · {fmtDate(leaveData.from)} to {fmtDate(leaveData.to)}
							</div>
							{leaveData.counts.leaves === 0
								? <div style={{ fontFamily: FONT, color: '#605E5C', padding: '40px 0' }}>No approved leaves in this period.</div>
								: <Chart options={leaveOptions} series={leaveData.series} type="bar" width="100%" height="350" />}
						</div>
					</div>
					<div className="card" style={cardStyle}>
						<div className="card-body">
							<h4 style={titleStyle}>Leave Type %</h4>
							<div style={subTitleStyle}>Share of leave days · {deptLabel}</div>
							{leaveData.counts.leaves > 0 &&
								<Chart options={leaveTypeOptions} series={leaveData.type_totals.map(t => t.days)} type="donut" width="100%" height="320" />}
						</div>
					</div>
					<div className="card" style={cardStyle}>
						<div className="card-body">
							<h4 style={titleStyle}>{leaveByDept ? 'Department Wise Leave Details' : 'Employee Leave Details'}</h4>
							<div style={subTitleStyle}>
								{leaveRows.length} {leaveByDept ? 'departments' : 'employees'} · {deptLabel} · days per leave type · % of total leave days
							</div>
							<div className="table-responsive mt-2" style={{ maxHeight: 480, overflowY: 'auto' }}>
								<table className="table table-hover table-striped table-vcenter mb-0" style={{ fontSize: 13 }}>
									<thead>
										<tr>
											<th>#</th>
											{leaveByDept
												? <><th>Department</th><th>Employees</th></>
												: <><th>Emp ID</th><th>Name</th><th>Designation</th></>}
											{leaveData.types.map(t => <th key={t}>{t}</th>)}
											<th>Total Days</th><th>%</th>
											{!leaveByDept && <th>Status</th>}
										</tr>
									</thead>
									<tbody>
										{leaveRows.length === 0 &&
											<tr><td colSpan={leaveData.types.length + 7} className="text-center">No approved leaves</td></tr>}
										{leaveRows.map((e, i) => (
											<tr key={(e.employee_id || e.department_name) + '-' + i}>
												<td>{i + 1}</td>
												{leaveByDept
													? <><td>{e.department_name}</td><td>{e.employees}</td></>
													: <><td>{e.employee_id}</td><td>{e.name}</td><td>{e.designation_name || '-'}</td></>}
												{leaveData.types.map(t => <td key={t}>{e.by_type[t] || '-'}</td>)}
												<td><b>{e.days}</b></td>
												<td style={{ minWidth: 110 }}>
													<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
														<div style={{ flex: 1, height: 6, background: '#EDEBE9' }}>
															<div style={{ width: e.percent + '%', height: 6, background: '#01B8AA' }} />
														</div>
														<span>{e.percent.toFixed(2)}%</span>
													</div>
												</td>
												{!leaveByDept && <td>
													{Number(e.is_active) === 1
														? <span className="tag tag-success">Active</span>
														: <span className="tag tag-danger">Inactive</span>}
												</td>}
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</>)}

				{isAbsentReport && loading && <div className="card" style={cardStyle}><div className="card-body">Loading…</div></div>}
				{isAbsentReport && !loading && !err && absentData && (<>
					<div className="row">
						{kpi('Absent Days', absentData.counts.days, '#FD625E')}
						{kpi('Employees', absentData.counts.employees, '#374649')}
					</div>
					<div className="card" style={cardStyle}>
						<div className="card-body">
							<h4 style={titleStyle}>Absent Days</h4>
							<div style={subTitleStyle}>
								By month · {deptLabel} · {fmtDate(absentData.from)} to {fmtDate(absentData.to)} · no clock in &amp; clock out
							</div>
							{absentData.counts.days === 0
								? <div style={{ fontFamily: FONT, color: '#605E5C', padding: '40px 0' }}>No absents in this period.</div>
								: <Chart options={absentOptions} series={[{ name: 'Absent Days', data: absentData.absent }]} type="bar" width="100%" height="350" />}
						</div>
					</div>
					<div className="card" style={cardStyle}>
						<div className="card-body">
							<h4 style={titleStyle}>{leaveByDept ? 'Department Wise Absent Count' : 'Employee Absent Count'}</h4>
							<div style={subTitleStyle}>
								{absentRows.length} {leaveByDept ? 'departments' : 'employees'} · {deptLabel} · % of total absent days
							</div>
							<div className="table-responsive mt-2" style={{ maxHeight: 480, overflowY: 'auto' }}>
								<table className="table table-hover table-striped table-vcenter mb-0" style={{ fontSize: 13 }}>
									<thead>
										<tr>
											<th>#</th>
											{leaveByDept
												? <><th>Department</th><th>Employees</th></>
												: <><th>Emp ID</th><th>Name</th><th>Designation</th></>}
											<th>Absent Days</th><th>%</th>
											{!leaveByDept && <th>Status</th>}
										</tr>
									</thead>
									<tbody>
										{absentRows.length === 0 &&
											<tr><td colSpan="7" className="text-center">No absents</td></tr>}
										{absentRows.map((e, i) => (
											<tr key={(e.employee_id || e.department_name) + '-' + i}>
												<td>{i + 1}</td>
												{leaveByDept
													? <><td>{e.department_name}</td><td>{e.employees}</td></>
													: <><td>{e.employee_id}</td><td>{e.name}</td><td>{e.designation_name || '-'}</td></>}
												<td><b>{e.days}</b></td>
												<td style={{ minWidth: 110 }}>
													<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
														<div style={{ flex: 1, height: 6, background: '#EDEBE9' }}>
															<div style={{ width: e.percent + '%', height: 6, background: '#FD625E' }} />
														</div>
														<span>{Number(e.percent).toFixed(2)}%</span>
													</div>
												</td>
												{!leaveByDept && <td>
													{Number(e.is_active) === 1
														? <span className="tag tag-success">Active</span>
														: <span className="tag tag-danger">Inactive</span>}
												</td>}
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</>)}

				{/* Power BI style card */}
				{!selectedType.months && <>
				{/* totals for the from – to months shown in the trend chart */}
				{!loading && !err && hasTrend && (
					<div className="row">
						{trendSeries.map((s, i) => kpi(
							`${s.name} Total · ${trend.labels[0]} – ${trend.labels[trend.labels.length - 1]}`,
							'₹ ' + inr(s.data.reduce((sum, v) => sum + Number(v || 0), 0)),
							COLORS[i]
						))}
					</div>
				)}
				<div className="card" style={cardStyle}>
					<div className="card-body">
						<h4 style={titleStyle}>{isPfEsi ? 'PF' : typeLabel}, {compareLabel}</h4>
						<div style={subTitleStyle}>By month · {deptLabel}</div>
						{loading && <div style={{ padding: '40px 0' }}>Loading…</div>}
						{!loading && !err && !hasTrend && noData}
						{!loading && !err && hasTrend && (
							<Chart options={trendOptions} series={trendSeries} type="bar" width="100%" height="400" />
						)}
					</div>
				</div>

				<div className="card" style={cardStyle}>
					<div className="card-body">
						<h4 style={titleStyle}>
							{breakdown?.series.map(s => s.name).join(', ') || typeLabel}
						</h4>
						<div style={subTitleStyle}>
							{breakdown?.group_by === 'employee' ? 'By employee' : 'By department'} · {periodLabel}
							{breakdown && bdLabels.length > 0 && <> · {breakdown.employees} employees</>}
						</div>
						{!loading && !err && bdLabels.length === 0 && noData}
						{!loading && !err && bdLabels.length > 0 && (
							// many departments / employees -> give each bar room and scroll sideways
							<div style={{ overflowX: 'auto' }}>
								<div style={{ minWidth: Math.max(bdLabels.length * 45, 600) }}>
									<Chart
										options={bdOptions}
										series={breakdown.series.map(s => ({ name: s.name, data: s.data }))}
										type="bar" width="100%" height="420"
									/>
								</div>
							</div>
						)}
					</div>
				</div>
				</>}
			</div>
		</div>
	);
};

const mapStateToProps = (state) => ({
	fixNavbar: state.settings.isFixNavbar,
});

export default connect(mapStateToProps)(PayrollBI);
